"use no memo";

import { ModuleTypes } from "@repo/database";
import { CashRegisterConfigurationSchema } from "@repo/validators";
import { createFileRoute } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";

import { SettingsHeader } from "~/components/layout/settings-header";
import { Button } from "~/components/ui/button";
import { Card, CardContent, CardFooter } from "~/components/ui/card";
import { Checkbox } from "~/components/ui/checkbox";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  useForm,
} from "~/components/ui/form";
import { ModalInput } from "~/components/ui/modal-input";
import ModuleNotActivated from "~/components/ui/module-not-activated";
import { MultiSelect } from "~/components/ui/multi-select";
import { useConfiguration } from "~/hooks/use-configuration";
import { useGeneral } from "~/hooks/use-general";
import { usePrinter } from "~/hooks/use-printer";
import { useUser } from "~/hooks/use-user";
import { useRegisterManager } from "~/managers/use-register-manager";
import { usePreferencesStore } from "~/stores/use-preferences-store";

export const Route = createFileRoute("/_main/settings/_layout/cash-register/")({
  component: CashRegister,
});

function CashRegister() {
  const { t, i18n } = useTranslation("common");

  const { updateCashRegisterConfiguration } = useRegisterManager();

  const { floorPlanMode, setFloorPlanMode } = usePreferencesStore();

  const { cashRegisterConfiguration } = useConfiguration();

  const { prices } = useGeneral();

  const { printRoutes } = usePrinter();

  const { modules } = useUser();

  const form = useForm({
    schema: CashRegisterConfigurationSchema(i18n),
    defaultValues: {
      tableInfoPopup: cashRegisterConfiguration.tableInfoPopup,
      defaultPriceId: cashRegisterConfiguration.defaultPriceId,
      nextRoundPrintRouteId: cashRegisterConfiguration.nextRoundPrintRouteId,
      orderAttentionCheck:
        cashRegisterConfiguration.orderAttentionCheckThreshold,
      orderAttentionLate: cashRegisterConfiguration.orderAttentionLateThreshold,
      autoFinalize: cashRegisterConfiguration.autoFinalize,
    },
  });

  const isDirty = !!Object.values(form.formState.dirtyFields).length;

  const handleSubmit = form.handleSubmit(async (formData) => {
    const result = await updateCashRegisterConfiguration(formData);
    if (result === true) {
      form.reset({}, { keepValues: true });
    }
  });

  return (
    <Form {...form}>
      <SettingsHeader title={t("cash_register")} />
      <form onSubmit={handleSubmit} className="flex flex-col gap-2">
        <Card className="overflow-hidden relative">
          <CardContent className="flex flex-col gap-2">
            {!modules.includes(ModuleTypes.cash_register) && (
              <ModuleNotActivated module={ModuleTypes.cash_register} />
            )}
            {modules.includes(ModuleTypes.cash_register) && (
              <>
                <FormField
                  control={form.control}
                  name="orderAttentionCheck"
                  render={({ field }) => (
                    <FormItem className="col-span-full">
                      <FormLabel>
                        {t("Config.order_attention_check_threshold")}
                      </FormLabel>
                      <ModalInput {...field} type="number" />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="orderAttentionLate"
                  render={({ field }) => (
                    <FormItem className="col-span-full">
                      <FormLabel>
                        {t("Config.order_attention_late_threshold")}
                      </FormLabel>
                      <ModalInput {...field} type="number" />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="defaultPriceId"
                  render={({ field }) => (
                    <FormItem className="col-span-full">
                      <FormLabel>
                        {t("Config.cash_register_default_price")}
                      </FormLabel>
                      <MultiSelect
                        maxSelect={1}
                        placeholder={t(
                          "Config.cash_register_default_price_placeholder",
                        )}
                        onValueChange={(value) => {
                          field.onChange(value[0]);
                        }}
                        options={prices.map((price) => ({
                          label: price.name,
                          value: price.id,
                        }))}
                        defaultValue={
                          field.value ? [field.value.toString()] : []
                        }
                        value={field.value}
                      />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="nextRoundPrintRouteId"
                  render={({ field }) => (
                    <FormItem className="col-span-full">
                      <FormLabel>
                        {t("Config.next_round_print_route")}
                      </FormLabel>
                      <MultiSelect
                        maxSelect={1}
                        placeholder={t(
                          "Config.next_round_print_route_placeholder",
                        )}
                        onValueChange={(value) => {
                          field.onChange(value[0]);
                        }}
                        options={printRoutes.map((printRoute) => ({
                          label: printRoute.name,
                          value: printRoute.id,
                        }))}
                        defaultValue={
                          field.value ? [field.value.toString()] : []
                        }
                        value={field.value}
                      />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="tableInfoPopup"
                  render={({ field }) => (
                    <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border p-4 shadow">
                      <FormControl>
                        <Checkbox
                          checked={field.value}
                          onCheckedChange={field.onChange}
                        />
                      </FormControl>
                      <div className="space-y-1 leading-none">
                        <FormLabel>{t("Config.table_popup_label")}</FormLabel>
                        <FormDescription>
                          {t("Config.table_popup_description")}
                        </FormDescription>
                      </div>
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="autoFinalize"
                  render={({ field }) => (
                    <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border p-4 shadow">
                      <FormControl>
                        <Checkbox
                          checked={field.value}
                          onCheckedChange={field.onChange}
                        />
                      </FormControl>
                      <div className="space-y-1 leading-none">
                        <FormLabel>Enable auto finalize</FormLabel>
                        <FormDescription>
                          When the order is fully paid, the order will be
                          automatically finalized.
                        </FormDescription>
                      </div>
                    </FormItem>
                  )}
                />
                <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border p-4 shadow">
                  <Checkbox
                    checked={floorPlanMode === 1}
                    onCheckedChange={() => {
                      if (floorPlanMode === 1) {
                        setFloorPlanMode(2);
                      } else {
                        setFloorPlanMode(1);
                      }
                    }}
                  />

                  <div className="space-y-1 leading-none">
                    <FormLabel>Floor plan / Table grid</FormLabel>
                  </div>
                </FormItem>
              </>
            )}
          </CardContent>
          <CardFooter>
            <Button disabled={!isDirty} type="submit" variant="success">
              {t("save")}
            </Button>
          </CardFooter>
        </Card>
      </form>
    </Form>
  );
}
